import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import {
  WATCHLIST_QUERY_KEY,
  WATCHLISTS_QUERY_KEY,
} from '@/hooks/useWatchlist';
import type {
  Watchlist,
  WatchlistQueryClientData,
  WatchlistsQueryClientData,
} from '@/hooks/useWatchlist';

import { mutationFn } from './queryFn';

export function useCreateWatchlist({
  onMutate,
  onSuccess,
  onError,
}: {
  onMutate?: () => void;
  onSuccess?: (watchlist: Watchlist) => void;
  onError?: () => void;
} = {}) {
  const queryClient = useQueryClient();

  const { mutate, status, error } = useMutation({
    mutationFn: (payload: { name: string }) => mutationFn(payload),
    onMutate,
    onSuccess: (watchlist) => {
      if (typeof watchlist === 'undefined') return;

      queryClient.setQueryData<WatchlistsQueryClientData>(
        WATCHLISTS_QUERY_KEY,
        (state) => {
          if (typeof state === 'undefined') {
            return state;
          }
          return produce(state, (draft) => {
            draft.push(watchlist);
          });
        }
      );
      /**
       * Explicitly set query data for the watchlist query based on the new watchlist's id.
       * Prefer this to `watchlistQueryClient`, which doesn't handle non-existing query data.
       * Finally, select the new watchlist.
       */
      queryClient.setQueryData<WatchlistQueryClientData>(
        [WATCHLIST_QUERY_KEY, watchlist.id],
        watchlist
      );
      onSuccess?.(watchlist);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLISTS_QUERY_KEY });
    },
    onError,
  });

  return {
    mutate,
    status,
    error,
  };
}
